import { switchMap } from 'rxjs/operators'
import { empty, merge, Observable, of, PartialObserver, Subscription } from 'rxjs';
import { defineComponent } from 'vue'

import { getAppLocaleFromI18nLocale, getI18nLocaleFromAppLocale } from '@skinsoft/utils'
import { bus, Message } from '@skinsoft/bus'
import { SubscriptionPool } from './subscription-pool'
import { SyncQueue } from './sync-queue'

// `import type` here is erased at compile time and never becomes a runtime `require`, so this
// augmentation can safely reach into packages (like record-mask) that themselves depend on
// reactive-vue without introducing a real circular dependency.
import type {
    AliasDataStore, ArchiveDataStore, AuditCampaignDataStore, ConditionReportDataStore,
    ContainerDataStore, EntityFieldDataStore, ExpressionDataStore, InstitutionDataStore,
    ItemDataStore, LocationDataStore, ManifestationDataStore, MediaDataStore, MovementDataStore,
    PrintableReportDataStore, RecordDataStore, SpecimenDataStore, TaskDataStore, ThesaurusDataStore,
    ThesaurusTermDataStore, TranslationDataStore, UserDataStore, WorkDataStore, WorkspaceDataStore,
} from '@skinsoft/entity'
import type { AuthService } from '@skinsoft/auth'
import type { ConditionReportPDFTransferHelper, ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import type { ConditionReportPDFGenerator } from '@skinsoft/pdf-generator'
import type { RecordMaskDataStore } from '@skinsoft/record-mask'
import type { MetaRecordMaskDataStore } from '@skinsoft/record-mask/src/meta-record-mask-data-store'

// The @vue/compat module-replacement shim (`export default`/`export *`) belongs in each app's
// `shims-vue.d.ts` instead: this file is a real module, so `declare module 'vue'` here can only be
// a type augmentation, which forbids that.

// TypeScript doesn't merge a `mixins` array's members into `this` (unlike a real class `extends`),
// so components using `mixins: [reactiveVueMixin]` fail to typecheck despite working at runtime.
// Augmenting `ComponentCustomProperties` once here fixes it for all of them without per-component
// casts. `pool`/`syncQueue$` are deliberately omitted: every component goes through the mixin now.
declare module 'vue' {
    interface ComponentCustomProperties {
        emitOnBus(topic: string, payload?: any): void
        subscribeToSyncQueue(observer: (() => any) | PartialObserver<void>): Subscription
        triggerSync(): void
        subscribeToBus(
            topic: string,
            observer: ((value: Message) => any) | PartialObserver<Message>,
            name?: string,
        ): Subscription
        subscribeTo<T = any>(
            source$: Observable<T>,
            observer: ((value: T) => any) | PartialObserver<T>,
            name?: string,
            notify$?: Observable<T>,
        ): Subscription
        unsubscribeFrom(name: string): void
        waitForUserApproval(message: string): void
        setAppLocale(locale: string): void
        getAppLocale(): string

        // Vue 3 dropped the instance event emitter; @vue/compat MODE 2 still runs it but no longer
        // types it, and a handful of components still call `this.$on(...)`. TODO: migrate them off it.
        $on(event: string | string[], callback: (...args: any[]) => void): void
        $off(event?: string | string[], callback?: (...args: any[]) => void): void
        $once(event: string, callback: (...args: any[]) => void): void

        // `vue-toasted` (no Vue 3 build) was replaced by the in-repo toast plugin
        // (`@skinsoft/ui` ToastPlugin), still wired through each app's configure-vue.ts.
        $toasted: { show(message: string, options?: any): { goAway(delay?: number): void } }

        // @vue/compat MODE 2 keeps Vue 2's separate `$listeners` (bootstrap-vue relies on it); a
        // couple of components still read `this.$listeners` directly.
        $listeners: Record<string, Function | Function[]>


        // Below: the `Vue.prototype.$xxx` properties each *Store/Service/Helper's `install(Vue)`
        // assigns (via `Vue.use(...)` in configure-vue.ts). Options API has no per-component
        // "declare only" field syntax to type these, so they're declared globally instead.
        $auditCampaignDataStore: AuditCampaignDataStore
        $recordDataStore: RecordDataStore
        $thesaurusTermDataStore: ThesaurusTermDataStore
        $thesaurusDataStore: ThesaurusDataStore
        $aliasDataStore: AliasDataStore
        // $collaboratorDataStore is assigned by both AliasDataStore.install() and the never-registered
        // AuthorityDataStore.install() (no app ever calls `Vue.use(AuthorityDataStore)`); AliasDataStore
        // is the one actually live at runtime.
        $collaboratorDataStore: AliasDataStore
        $printableReportDataStore: PrintableReportDataStore
        $userDataStore: UserDataStore
        $containerDataStore: ContainerDataStore
        $archiveDataStore: ArchiveDataStore
        $workspaceDataStore: WorkspaceDataStore
        $expressionDataStore: ExpressionDataStore
        $manifestationDataStore: ManifestationDataStore
        $institutionDataStore: InstitutionDataStore
        $itemDataStore: ItemDataStore
        $mediaDataStore: MediaDataStore
        $movementDataStore: MovementDataStore
        $entityFieldDataStore: EntityFieldDataStore
        $taskDataStore: TaskDataStore
        $conditionReportDataStore: ConditionReportDataStore
        $translationDataStore: TranslationDataStore
        $workDataStore: WorkDataStore
        $locationDataStore: LocationDataStore
        $specimenDataStore: SpecimenDataStore

        $authService: AuthService

        $mediaTransferHelper: MediaTransferHelper
        $imageTransferHelper: ImageTransferHelper
        $conditionReportPDFTransferHelper: ConditionReportPDFTransferHelper

        $conditionReportPDFGenerator: ConditionReportPDFGenerator

        $recordMaskDataStore: RecordMaskDataStore
        $metaRecordMaskDataStore: MetaRecordMaskDataStore
    }
}

// The former `ReactiveVue` base class was removed: Vue 3 no longer supports extending the Vue
// constructor, and every component now uses `mixins: [reactiveVueMixin]` instead.

// Options API equivalent, provided as a mixin (Vue has no class inheritance in Options API form).
export const reactiveVueMixin = defineComponent({
    data() {
        return {
            pool: new SubscriptionPool(),
            syncQueue$: new SyncQueue(),
        }
    },
    methods: {
        emitOnBus(topic: string, payload?: any) {
            bus.emit(topic, payload);
        },

        subscribeToSyncQueue(observer: () => any | PartialObserver<void>) {
            if (!this.syncQueue$.started) {
                this.pool.add(this.syncQueue$.start());
            }

            return this.subscribeTo(this.syncQueue$, observer);
        },

        triggerSync() {
            this.syncQueue$.tick();
        },

        subscribeToBus(
            topic: string,
            observer: ((value: Message) => any) | PartialObserver<Message>,
            name?: string,
        ) {
            return this.subscribeTo(bus.listen$(topic), observer, name);
        },

        subscribeTo<T = any>(
            source$: Observable<T>,
            observer: ((value: T) => any) | PartialObserver<T>,
            name?: string,
            notify$?: Observable<T>,
        ) {
            let observerNoTypeAmbiguity
            if (typeof observer === 'function') {
                observerNoTypeAmbiguity = observer as (value: T) => any
            } else {
                observerNoTypeAmbiguity = observer as PartialObserver<T>
            }
            const subscription = merge(of(true), notify$ || empty())
                .pipe(switchMap(() => source$))
                .subscribe(observerNoTypeAmbiguity);
            this.pool.add(subscription, name);

            return subscription;
        },

        unsubscribeFrom(name: string) {
            const subscription = this.pool.get(name);

            if (subscription) {
                subscription.unsubscribe();
            }
        },

        waitForUserApproval(message) {
            if (!window.confirm(message)) {
                throw new Error('User denied approval');
            }
        },

        /**
         * Set the appLocale to the specified value, using the mapping between appLocale and i18nLocale.
         * "appLocale" is the locale key to use for TranslatedString (ex: "fr") and "i18nLocale" is the locale
         * key to use for Reporter translations (ex: "fr-DIOR")
         * @param locale
         */
        setAppLocale(locale: string) {
            this.$i18n.locale = getI18nLocaleFromAppLocale(locale) ?? locale
        },

        /**
         * Get appLocale
         * "appLocale" is the locale key to use for TranslatedString (ex: "fr") and "i18nLocale" is the locale
         * key to use for Reporter translations (ex: "fr-DIOR")
         */
        getAppLocale(): string {
            return getAppLocaleFromI18nLocale(this.$i18n.locale) ?? this.$i18n.locale
        },
    },
    beforeUnmount() {
        this.pool.flush();
    },
})
